import React, { useState } from 'react';
import { services, comingSoonServices } from '../mock';
import { BookOpen, Users, Heart, ArrowRight, Calendar, Gift, HandHeart, Church, ShoppingBag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getServiceIcon = (title) => {
  if (title.includes('Coaching')) return <Users size={32} />;
  if (title.includes('12-Week')) return <Calendar size={32} />;
  if (title.includes('Prayer')) return <HandHeart size={32} />;
  if (title.includes('Devotionals')) return <Heart size={32} />;
  if (title.includes('Bible Studies')) return <Church size={32} />;
  if (title.includes('Pop-Up')) return <ShoppingBag size={32} />;
  if (title.includes('Book')) return <BookOpen size={32} />;
  return <Heart size={32} />;
};

export const Services = () => {
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleServiceInquiry = async (serviceTitle) => {
    if (serviceTitle.includes('Prayer')) {
      // Redirect to special prayer request flow
      const element = document.querySelector('#contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      alert('Please scroll down to the Contact section to submit your prayer request, or call us directly. We\'ll call you to pray together!');
      return;
    }

    // For other services, collect basic info and submit inquiry
    const name = prompt('Please enter your name:');
    if (!name) return;

    const email = prompt('Please enter your email address:');
    if (!email) return;

    const phone = prompt('Please enter your phone number (optional):') || '';

    setIsSubmitting(true);
    setInquiryMessage('');

    try {
      const response = await fetch(`${API}/service-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          phone: phone || null,
          service_type: serviceTitle,
          message: `Interested in learning more about ${serviceTitle}`
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setInquiryMessage(`Thank you ${name}! We'll get back to you soon about ${serviceTitle}. Check your email for confirmation.`);
      } else {
        setInquiryMessage(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Service inquiry error:', error);
      setInquiryMessage('Unable to send inquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComingSoonSignup = async (serviceTitle) => {
    const email = prompt('Enter your email to be notified when this becomes available:');
    if (!email) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/coming-soon-notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          service_type: serviceTitle
        }),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Thank you! You'll be the first to know when ${serviceTitle} becomes available.`);
      } else {
        alert(result.error || 'Unable to sign up for notifications. Please try again.');
      }
    } catch (error) {
      console.error('Coming soon signup error:', error);
      alert('Unable to sign up right now. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPricing = (service) => {
    if (service.pricing && typeof service.pricing === 'object') {
      // Handle complex pricing structure
      return (
        <div>
          {service.pricing.firstSession && (
            <p className="body-small" style={{ 
              color: '#8B4513', 
              marginBottom: '4px',
              fontWeight: '600' 
            }}>
              ✨ {service.pricing.firstSession}
            </p>
          )}
          {service.pricing.ongoing && (
            <p className="body-small" style={{ color: '#5D4037', marginBottom: '4px' }}>
              {service.pricing.ongoing}
            </p>
          )}
          {service.pricing.package && (
            <p className="body-small" style={{ color: '#8D6E63', marginBottom: '4px' }}>
              {service.pricing.package}
            </p>
          )}
          {service.pricing.standalone && (
            <p className="body-small" style={{ color: '#5D4037', marginBottom: '4px' }}>
              {service.pricing.standalone}
            </p>
          )}
          {service.pricing.withCoaching && (
            <p className="body-small" style={{ 
              color: '#8B4513', 
              marginBottom: '4px',
              fontWeight: '600'
            }}>
              ✨ {service.pricing.withCoaching}
            </p>
          )}
          {service.pricing.comingSoon && (
            <p className="body-small" style={{ color: '#CD853F', marginBottom: '4px' }}>
              {service.pricing.comingSoon}
            </p>
          )}
        </div>
      );
    } else {
      // Simple pricing
      return (
        <p className="body-small" style={{ color: '#5D4037', marginBottom: '4px' }}>
          {service.pricing}
        </p>
      );
    }
  };

  return (
    <section id="services" className="section-padding" style={{ 
      background: 'linear-gradient(135deg, #FFF8DC 0%, #F5F5DC 100%)'
    }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px', color: '#8B4513' }}>
            Our Services
          </h2>
          <p className="body-large" style={{ color: '#5D4037', maxWidth: '700px', margin: '0 auto' }}>
            Discover the various ways we can support you on your journey of healing, growth, and spiritual transformation.
          </p>
        </div>

        {/* Current Services */}
        <div className="service-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '32px',
          marginBottom: '80px'
        }}>
          {services.map((service) => (
            <div
              key={service.title}
              style={{
                background: 'rgba(255, 248, 220, 0.8)',
                border: '1px solid #D2B48C',
                borderRadius: '0px',
                padding: '32px',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-4px)';
                e.target.style.boxShadow = '0 8px 32px rgba(139, 69, 19, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <div style={{ 
                color: '#8B4513', 
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {getServiceIcon(service.title)}
              </div>
              
              <h3 className="heading-2" style={{ marginBottom: '16px', color: '#8B4513' }}>
                {service.title}
              </h3>
              
              <p className="body-regular" style={{ marginBottom: '24px', color: '#5D4037' }}>
                {service.description}
              </p>
              
              <div style={{ marginBottom: '24px' }}>
                {renderPricing(service)}
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Special handling for Featured Book */}
                {service.title.includes('Book') ? (
                  <button
                    onClick={() => window.open(service.bookLink, '_blank')}
                    style={{
                      background: '#8B4513',
                      color: 'white',
                      border: '2px solid #8B4513',
                      borderRadius: '0px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#A0522D';
                      e.target.style.borderColor = '#A0522D';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#8B4513';
                      e.target.style.borderColor = '#8B4513';
                    }}
                  >
                    Get Book on Amazon
                    <ArrowRight size={16} style={{ marginLeft: '8px' }} />
                  </button>
                ) : (
                  /* Regular service buttons */
                  <button
                    className="btn-primary"
                    onClick={() => {
                      if (service.title.includes('Coaching') || service.title.includes('12-Week')) {
                        // Open Calendly booking for coaching and transformation program
                        window.open('https://calendly.com/khesed-avad', '_blank');
                      } else if (service.title.includes('Prayer')) {
                        // Redirect to special prayer request flow
                        const element = document.querySelector('#contact');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      } else {
                        handleServiceInquiry(service.title);
                      }
                    }}
                    disabled={isSubmitting}
                    style={{
                      background: '#8B4513',
                      borderColor: '#8B4513',
                      color: 'white',
                      padding: '12px 24px',
                      border: '2px solid #8B4513',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isSubmitting ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#A0522D';
                      e.target.style.borderColor = '#A0522D';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#8B4513';
                      e.target.style.borderColor = '#8B4513';
                    }}
                  >
                    {service.title.includes('Prayer') ? 'Submit Prayer Request' : 
                     service.title.includes('Coaching') ? 'Book Free Session' :
                     service.title.includes('12-Week') ? 'Schedule Consultation' : 
                     'Learn More'}
                    <ArrowRight size={16} style={{ marginLeft: '8px' }} />
                  </button>
                )}
                
                {(service.title.includes('Coaching') || service.title.includes('12-Week')) && (
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      window.open('/intake-form', '_blank');
                    }}
                    style={{
                      background: 'transparent',
                      border: '2px solid #CD853F',
                      borderRadius: '0px',
                      color: '#8B4513',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#CD853F';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#8B4513';
                    }}
                  >
                    Complete Intake Form
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Coming Soon Services */}
        <div style={{ 
          background: 'rgba(139, 69, 19, 0.05)',
          border: '2px solid #D2B48C',
          borderRadius: '0px',
          padding: '48px',
          textAlign: 'center'
        }}>
          <h3 className="heading-1" style={{ marginBottom: '24px', color: '#8B4513' }}>
            Coming Soon
          </h3>
          <p className="body-large" style={{ marginBottom: '48px', color: '#5D4037' }}>
            We&apos;re expanding our ministry to serve you even better. Be the first to know when these new services become available!
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {comingSoonServices.map((service) => (
              <div
                key={service.title}
                style={{
                  background: 'rgba(255, 248, 220, 0.6)',
                  border: '1px solid #D2B48C',
                  borderRadius: '0px',
                  padding: '24px',
                  textAlign: 'center'
                }}
              >
                <div style={{ 
                  color: '#CD853F', 
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {getServiceIcon(service.title)}
                </div>
                
                <h4 className="heading-3" style={{ marginBottom: '12px', color: '#8B4513' }}>
                  {service.title}
                </h4>
                
                <p className="body-small" style={{ marginBottom: '16px', color: '#5D4037' }}>
                  {service.description}
                </p>
                
                <button
                  className="btn-secondary"
                  onClick={() => handleComingSoonSignup(service.title)}
                  disabled={isSubmitting}
                  style={{
                    background: 'transparent',
                    border: '2px solid #CD853F',
                    borderRadius: '0px',
                    color: '#8B4513',
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#CD853F';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#8B4513';
                  }}
                >
                  Notify Me
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Success/Error Message */}
        {inquiryMessage && (
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: inquiryMessage.includes('Thank you') ? 'rgba(139, 69, 19, 0.1)' : 'rgba(205, 133, 63, 0.1)',
            border: `1px solid ${inquiryMessage.includes('Thank you') ? '#8B4513' : '#CD853F'}`,
            borderRadius: '0px',
            textAlign: 'center',
            color: '#5D4037'
          }}>
            {inquiryMessage}
          </div>
        )}
      </div>
    </section>
  );
};